import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import {
  registerPayment,
  registerAdminRentalPayment,
} from '../../Redux/Actions/actions';
import { RENTAL_TYPE, typeLabel } from '../../Views/RentalManagement/utils/rentalTypeHelpers';
import style from './RegisterPaymentModal.module.css';

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Efectivo' },
  { value: 'transferencia', label: 'Transferencia' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'tarjeta', label: 'Tarjeta' },
  { value: 'mercadopago', label: 'Mercado Pago' },
  { value: 'otro', label: 'Otro' },
];

/**
 * Modal unificado para registrar el pago de una cuota específica.
 *
 * Props:
 * - paymentId: string | number (id de la cuota)
 * - paymentInfo?: { paymentNumber, dueDate, totalAmount, currency, propertyAddress, ownerName }
 * - type: 'regular' | 'admin'
 * - onClose: () => void
 * - onRegistered?: () => void
 */
const RegisterPaymentModal = ({
  paymentId,
  paymentInfo,
  type = RENTAL_TYPE.REGULAR,
  onClose,
  onRegistered,
}) => {
  const dispatch = useDispatch();
  const [amountPaid, setAmountPaid] = useState(
    paymentInfo?.totalAmount ? String(paymentInfo.totalAmount) : ''
  );
  const [paymentDate, setPaymentDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [paymentMethod, setPaymentMethod] = useState('transferencia');
  const [receiptNumber, setReceiptNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isAdmin = type === RENTAL_TYPE.ADMIN;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amount = Number(amountPaid);
    if (!amountPaid || isNaN(amount) || amount <= 0) {
      setError('Ingresá un monto válido');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload = {
        amountPaid: amount,
        paymentDate,
        paymentMethod,
        receiptNumber: receiptNumber.trim() || undefined,
        notes: notes.trim() || undefined,
      };
      if (isAdmin) {
        await dispatch(registerAdminRentalPayment(paymentId, payload));
      } else {
        // El backend regular usa { amount, ... }; mantenemos compat pasando ambos.
        await dispatch(
          registerPayment(paymentId, {
            ...payload,
            amount,
          })
        );
      }
      toast.success('Pago registrado correctamente');
      onRegistered?.();
      onClose?.();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'No se pudo registrar el pago';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={style.overlay} onClick={onClose}>
      <div className={style.modal} onClick={(e) => e.stopPropagation()}>
        <h3 className={style.title}>
          Registrar Pago
          <span className={`${style.typeBadge} ${isAdmin ? style.admin : ''}`}>
            {typeLabel(type)}
          </span>
        </h3>
        <p className={style.subtitle}>
          Registrá el cobro realizado por el inquilino para esta cuota.
        </p>

        {paymentInfo && (
          <div className={style.summary}>
            <div>
              <strong>Cuota #{paymentInfo.paymentNumber}</strong>
            </div>
            <div>
              Vence:{' '}
              <strong>
                {paymentInfo.dueDate
                  ? new Date(paymentInfo.dueDate).toLocaleDateString('es-AR')
                  : '-'}
              </strong>
            </div>
            <div>
              Monto:{' '}
              <strong>
                ${Number(paymentInfo.totalAmount || 0).toLocaleString('es-AR')}
              </strong>
            </div>
            {paymentInfo.propertyAddress && (
              <div>
                {paymentInfo.propertyAddress}
                {paymentInfo.ownerName && ` · ${paymentInfo.ownerName}`}
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {error && <div className={style.error}>{error}</div>}
          <div className={style.formRow}>
            <div className={style.formGroup}>
              <label>Monto cobrado *</label>
              <input
                className={style.input}
                type="number"
                step="0.01"
                min="0"
                value={amountPaid}
                onChange={(e) => setAmountPaid(e.target.value)}
                required
              />
            </div>
            <div className={style.formGroup}>
              <label>Fecha de pago</label>
              <input
                className={style.input}
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
              />
            </div>
          </div>
          <div className={style.formRow}>
            <div className={style.formGroup}>
              <label>Método</label>
              <select
                className={style.select}
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                {PAYMENT_METHODS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
            <div className={style.formGroup}>
              <label>Nº de recibo</label>
              <input
                className={style.input}
                type="text"
                value={receiptNumber}
                onChange={(e) => setReceiptNumber(e.target.value)}
                placeholder="Opcional"
              />
            </div>
          </div>
          <div className={style.formGroup}>
            <label>Notas</label>
            <textarea
              className={style.textarea}
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Opcional"
            />
          </div>
          <div className={style.actions}>
            <button
              type="button"
              className={`${style.button} ${style.secondary}`}
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className={`${style.button} ${style.primary}`}
              disabled={loading}
            >
              {loading ? 'Registrando...' : 'Registrar pago'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterPaymentModal;
